import axios from 'axios';

// auth-service base. Endpoints live under /api/v1/...
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

// Roles that grant admin-portal access (tolerant of canonical + legacy naming).
// RBAC Model A: the admin-portal is the platform operator console, so the
// canonical platform_* roles are the primary admins here; the legacy
// super_admin/system_admin names are aliases that map to platform scope.
const PLATFORM_ROLES = [
  'platform_owner',
  'platform_admin',
  'platform_support',
  'platform_billing',
  'platform_analyst',
];
const SYSTEM_ADMIN_ROLES = ['super_admin', 'system_admin', 'system-admin', ...PLATFORM_ROLES];
const TENANT_ADMIN_ROLES = ['admin', 'tenant_admin', 'tenant-admin', 'tenant_owner'];
const ADMIN_ROLES = [...SYSTEM_ADMIN_ROLES, ...TENANT_ADMIN_ROLES];

function rolesFrom(src: { roles?: string[]; role?: string }): string[] {
  if (Array.isArray(src.roles) && src.roles.length) return src.roles;
  return src.role ? [src.role] : [];
}

function isAdmin(roles: string[]): boolean {
  return roles.some((r) => ADMIN_ROLES.includes(r));
}

/** Map the user's roles to the admin-portal's role union. */
function portalRole(roles: string[]): 'system-admin' | 'tenant-admin' {
  return roles.some((r) => SYSTEM_ADMIN_ROLES.includes(r)) ? 'system-admin' : 'tenant-admin';
}

class AuthService {
  // P2-8: the access token now lives in an httpOnly cookie (unreadable by JS).
  // We hold only the session-bound CSRF token (in memory) for state-changing calls.
  private csrf: string | null = null;

  constructor() {
    // Purge any legacy localStorage bearer token from before the migration.
    try { localStorage.removeItem('admin_token'); localStorage.removeItem('admin_refresh_token'); } catch { /* ignore */ }
    this.setupInterceptors();
  }

  getCsrf(): string | null {
    return this.csrf;
  }

  private setupInterceptors() {
    // Send the httpOnly session cookie on every request.
    axios.defaults.withCredentials = true;

    // Request interceptor: no Authorization bearer (the cookie carries auth).
    // Attach the session-bound CSRF token on state-changing requests only.
    axios.interceptors.request.use(
      (config) => {
        const method = (config.method || 'get').toUpperCase();
        if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) && this.csrf) {
          config.headers = config.headers || {};
          (config.headers as Record<string, string>)['X-CSRF-Token'] = this.csrf;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor: on 401 try ONE cookie-based refresh then retry. The
    // __isRetry flag + skipping /auth/* prevents infinite loops / refresh storms.
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const cfg = (error.config || {}) as Record<string, any>;
        const url = String(cfg.url || '');
        if (error.response?.status === 401 && !cfg.__isRetry && !url.includes('/auth/')) {
          cfg.__isRetry = true;
          try {
            await this.refreshToken();
            return axios.request(cfg);
          } catch {
            await this.logout();
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * P2-8: establish the session from the httpOnly cookie via the gateway. Returns
   * the admin user (and captures the CSRF token) or null when unauthenticated /
   * not an admin. Replaces the old "decode the stored JWT" hydration.
   */
  async hydrateSession(): Promise<Record<string, unknown> | null> {
    try {
      const { data } = await axios.get(`${API_URL}/auth/session`);
      if (!data?.authenticated || !data.user) { this.csrf = null; return null; }
      const roles = rolesFrom(data.user);
      if (!isAdmin(roles)) { this.csrf = null; return null; }
      this.csrf = data.csrfToken || null;
      const user = {
        id: data.user.id,
        email: data.user.email,
        name: data.user.email,
        role: portalRole(roles),
        roles,
        tenantId: data.user.tenantId,
        permissions: [],
      };
      localStorage.setItem('admin_user', JSON.stringify(user));
      return user;
    } catch {
      this.csrf = null;
      return null;
    }
  }

  /** Fetch the session-bound CSRF token after a flow that (re)set the cookie. */
  private async captureCsrf(): Promise<void> {
    try {
      const { data } = await axios.get(`${API_URL}/auth/session`);
      this.csrf = data?.csrfToken || null;
    } catch {
      this.csrf = null;
    }
  }

  /** Direct email/password login against auth-service. workspace = tenant slug.
   *  P2-8: the backend sets the httpOnly session cookie; we never store the token.
   *  We read the response body only for the (richer) display user, then capture
   *  the CSRF token bound to the new session. */
  async login(email: string, password: string, workspace: string) {
    const { data } = await axios.post(`${API_URL}/auth/login`, { email, password, workspace });
    const roles = rolesFrom(data.user || {});
    if (!isAdmin(roles)) {
      throw new Error('Admin access required: this account is not an administrator');
    }
    const user = {
      id: data.user.id,
      email: data.user.email,
      name: data.user.fullName || data.user.email,
      role: portalRole(roles),
      roles,
      tenantId: data.tenant?.id,
      tenantName: data.tenant?.name,
      tenantSlug: data.tenant?.slug || workspace,
      tenantLogo: data.tenant?.logoUrl || undefined,
      permissions: data.user.permissions || [],
    };
    await this.captureCsrf();
    this.persist(user);
    return { user, token: '' };
  }

  /**
   * P2-8: adopt a JWT from an SSO redirect (main-app) into the httpOnly cookie via
   * the gateway (same-origin), then hydrate the admin session from /session. The
   * token is never stored in JS.
   */
  async validateToken(token: string) {
    await axios.post(`${API_URL}/auth/session/adopt`, { accessToken: token });
    const user = await this.hydrateSession();
    if (!user) {
      throw new Error('Admin access required');
    }
    return { user, token: '' };
  }

  /** SSO entry from main-app: the token IS a real auth-service JWT. */
  async loginWithSSO(token: string, _role?: string) {
    return this.validateToken(token);
  }

  /** Change the signed-in user's password (Authorization header added by the interceptor). */
  async changePassword(currentPassword: string, newPassword: string) {
    await axios.post(`${API_URL}/auth/change-password`, { currentPassword, newPassword });
  }

  /**
   * Request a password-reset email. The admin-portal is the platform operator
   * console, so the workspace is fixed to 'platform' (operators never type a slug).
   */
  async forgotPassword(email: string, workspace = 'platform') {
    await axios.post(`${API_URL}/auth/forgot-password`, { email, workspace });
  }

  /** Complete a password reset using the token from the email link. */
  async resetPassword(token: string, newPassword: string) {
    await axios.post(`${API_URL}/auth/reset-password`, { token, newPassword });
  }

  // P2-8: persist only the display user (not a credential). The token is in the
  // httpOnly cookie; admin_user is UI convenience and the backend enforces auth.
  private persist(user: unknown) {
    localStorage.setItem('admin_user', JSON.stringify(user));
  }

  // P2-8: refresh via the httpOnly refresh cookie (no token in JS). The backend
  // rotates both cookies; re-capture the CSRF token bound to the new session.
  async refreshToken() {
    await axios.post(`${API_URL}/auth/refresh`, {});
    await this.captureCsrf();
    return {};
  }

  async logout() {
    try {
      await axios.post(`${API_URL}/auth/logout`, {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.csrf = null;
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      localStorage.removeItem('admin_refresh_token');
    }
  }

  getCurrentUser() {
    const userStr = localStorage.getItem('admin_user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // P2-8: the token is httpOnly and unreadable by JS — there is no token to hand
  // out. Kept as a null-returning stub for any legacy caller; nothing should
  // build an Authorization header anymore (auth rides the cookie).
  getToken(): string | null {
    return null;
  }

  // Display-state proxy for "logged in" (admin_user is set on login/hydrate,
  // cleared on logout). The backend remains the authority on every request.
  isAuthenticated() {
    return !!localStorage.getItem('admin_user');
  }
}

export const authService = new AuthService();